import { useEffect, useState } from "react";
import { Check } from "lucide-react";

import { api, type ParameterVersionList, type SettingsResponse } from "@/api/client";
import { useTradingData } from "@/context/TradingDataContext";

/**
 * Bande de synthèse Réglages : chips de mode (/api/settings), la version de
 * paramètres ACTIVE (#id · hash8 — « défaut compilé » sans pointeur) et le build.
 * L'ex-compteur de drift dupliquait les chips « ≠ défaut » du rail, même viewport —
 * supprimé. §25 : un /api/parameters en échec affiche « ? », jamais une version
 * inventée.
 */
export default function SettingsStrip({ settings }: { settings: SettingsResponse }) {
  const { health } = useTradingData();
  const [params, setParams] = useState<ParameterVersionList | null>(null);
  const [paramsFailed, setParamsFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listParameters()
      .then((l) => {
        if (!cancelled) setParams(l);
      })
      .catch(() => {
        if (!cancelled) setParamsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const active = params?.versions.find((v) => v.activePointer) ?? null;

  return (
    <div
      className="border-border bg-surface-2/60 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-2 text-xs backdrop-blur-sm"
      data-testid="settings-strip"
      aria-label="Synthèse de configuration"
    >
      <Chip tone="info">{settings.mode.botMode} · BOT</Chip>
      <Chip tone="idle">IG_API {settings.mode.igApiMode}</Chip>
      <Sep />
      <span className="text-muted-foreground">Set actif</span>
      {paramsFailed ? (
        <Chip tone="idle">?</Chip>
      ) : active ? (
        <Chip tone="buy" testId="settings-strip-active">
          <Check className="size-3" aria-hidden />
          #{active.id} · {(active.paramsHash ?? "").slice(0, 8) || "—"}
        </Chip>
      ) : (
        <Chip tone="idle" testId="settings-strip-active">
          défaut compilé
        </Chip>
      )}
      <span className="flex-1" />
      <span className="text-dim font-mono tabular-nums">
        backend {health?.version ?? "?"} · {health?.commit ?? "?"}
      </span>
    </div>
  );
}

function Sep() {
  return <span className="bg-border-strong h-4 w-px" aria-hidden />;
}

function Chip({
  tone,
  children,
  testId,
}: {
  tone: "info" | "idle" | "buy" | "warning";
  children: React.ReactNode;
  testId?: string;
}) {
  const cls = {
    info: "text-info border-info/45 bg-info-soft",
    idle: "text-muted-foreground border-idle/45 bg-idle-soft",
    buy: "text-buy border-buy/45 bg-buy-soft",
    warning: "text-warning border-warning/45 bg-warning-soft",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-semibold whitespace-nowrap ${cls}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}
